import {
  ConsentPreferences,
  DEFAULT_CONSENT_PREFERENCES,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from './types';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * SSR-safe localStorage wrapper for consent preferences
 * Only interacts with localStorage on client side
 */
export class ConsentStorage {
  /**
   * Check if we're running in browser environment
   */
  private static isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }

  /**
   * Load consent preferences from localStorage
   * Returns null if no consent has been given yet
   */
  static load(): ConsentPreferences | null {
    if (!this.isBrowser()) {
      return null;
    }

    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const preferences = JSON.parse(stored) as ConsentPreferences;

      // Check if consent version has changed
      if (preferences.version !== CONSENT_VERSION) {
        // Version mismatch - require new consent
        this.clear();
        return null;
      }

      // Ensure necessary cookies are always enabled
      preferences.necessary = true;

      return preferences;
    } catch (error) {
      logger.error('Failed to load consent preferences:', error);
      return null;
    }
  }

  /**
   * Save consent preferences to localStorage
   */
  static save(preferences: ConsentPreferences): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      // Ensure necessary cookies are always enabled
      const safePreferences: ConsentPreferences = {
        ...preferences,
        necessary: true,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION,
      };

      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(safePreferences));
    } catch (error) {
      logger.error('Failed to save consent preferences:', error);
    }
  }

  /**
   * Clear stored consent preferences
   */
  static clear(): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to clear consent preferences:', error);
    }
  }

  /**
   * Check if consent has been given
   */
  static hasConsent(): boolean {
    return this.load() !== null;
  }

  /**
   * Get default consent (reject all except necessary)
   */
  static getDefault(): ConsentPreferences {
    return {
      ...DEFAULT_CONSENT_PREFERENCES,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  }

  /**
   * Get "accept all" consent preferences
   */
  static getAcceptAll(): ConsentPreferences {
    return {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  }

  /**
   * Get "reject all" consent preferences (only necessary)
   */
  static getRejectAll(): ConsentPreferences {
    return {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  }
}
